import React, { useState, useEffect, useCallback } from "react";

import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { PageStandard } from "components/admin/PageStandard";
import { BlockStandard } from "components/admin/BlockStandard";
import { Environment, SceneAction } from "api/automationTypes";
import { Thermostat } from "components/automation/Thermostat";
import { createOrUpdateScene, fetchSceneDetails } from "api/automation";
import { useToken, useUserContext } from "contexts/UserContext";
import { FilteredList } from "components/MUIExtensions/FilteredList";
import { ListItemSpaces } from "components/automation/ListItemSpaces";
import { useErrorInfo, useSetError } from "contexts/ErrorContext";
import { CustomCodes, ErrorResponse } from "contexts/ErrorContextData";

const WEEKDAY_NAMES = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];

const sanitizeTimePartInput = (value: string) =>
  value.replace(/\D/g, "").slice(0, 2);

const parseTimeParts = (
  hoursText: string,
  minutesText: string,
  referenceDate: Date | null
) => {
  if (!hoursText.length || !minutesText.length) {
    return null;
  }

  const hours = Number(hoursText);
  const minutes = Number(minutesText);

  if (hours > 23 || minutes > 59) {
    return null;
  }

  const next = referenceDate ? new Date(referenceDate) : new Date(2002, 1, 2);
  next.setHours(hours, minutes, 0, 0);
  return next;
};

const useStyles = makeStyles()((theme) => ({
  toggleButtonGroup: {
    justifyContent: "space-between",
  },
  toggleButton: {
    "&&.MuiToggleButton-root": {
      borderRadius: "100px",
      border: `1px solid ${theme.palette.secondary.light}`,
      flex: 0,
      padding: "10px",
      minWidth: "40px",

      "& > p": {
        color: theme.palette.secondary.light,
      },

      "&.Mui-selected": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main,

        "& > p": {
          color: theme.palette.common.white,
        },
      },
    },
  },
  timeInputWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  timePartField: {
    width: 88,

    "& input": {
      textAlign: "center",
      fontSize: theme.typography.h4.fontSize,
      fontWeight: 600,
      padding: theme.spacing(1.5, 1),
    },
  },
  timeFieldsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
  timeLabelsRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.25),
  },
  timePartLabel: {
    display: "block",
    width: 88,
    textAlign: "center",
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.2,
  },
  timeSeparator: {
    fontSize: theme.typography.h4.fontSize,
    fontWeight: 600,
    lineHeight: 1,
    color: theme.palette.text.primary,
  },
  timeHint: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.secondary.main,
    fontWeight: 400,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.3,
    textAlign: "center",
  },
}));

const formatRequestData = (
  name,
  actionCode,
  spaces,
  timeDate: Date,
  rootSpaceId,
  temperature,
  includedDays,
  sceneId
) => {
  const action =
    actionCode === SceneAction.AC_ON
      ? "SCENE_ACTION_AC_ON"
      : "SCENE_ACTION_AC_OFF";
  const triggerTime = timeDate.toLocaleTimeString("pt-br", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const weekMap = WEEKDAY_NAMES.reduce(
    (acc, val) => acc + (includedDays.includes(val) ? "1" : "0"),
    ""
  );
  const members = spaces
    .filter((space) => space.isMember)
    .map(({ id, type }) => ({ id, type }));

  const formattedReqData = {
    action,
    members,
    name,
    rootSpaceId,
    temperature,
    triggerTime,
    weekMap,
  };

  if (sceneId && sceneId !== "new") {
    formattedReqData["id"] = sceneId;
  }

  return formattedReqData;
};

export const AutomationEdit = ({
  match: {
    params: { id },
  },
  history,
}) => {
  const token = useToken();
  const setError = useSetError();
  const error = useErrorInfo();
  const { currentSpace } = useUserContext();
  const { classes } = useStyles();
  const [isLoading, setIsLoading] = useState<
    "submit" | "scene" | "initial" | false
  >("initial");

  const [name, setName] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [isNameValid, setIsNameValid] = useState<boolean>(true);
  const [action, setAction] = useState<SceneAction>(1);
  const [dayStatus, setDayStatus] = useState<Array<string>>([]);
  const [time, setTime] = useState<Date | null>(null);
  const [hoursText, setHoursText] = useState("");
  const [minutesText, setMinutesText] = useState("");
  const [temperature, setTemperature] = useState<number>(23);
  const [environments, setEnvironments] = useState<Array<Environment>>([]);

  const loadScene = useCallback(
    async (sceneId: string) => {
      setIsLoading("scene");
      try {
        const data = await fetchSceneDetails(
          token,
          currentSpace?.id || "",
          sceneId
        );

        if (sceneId) {
          setAction(
            data.action === "SCENE_ACTION_AC_ON"
              ? SceneAction.AC_ON
              : SceneAction.AC_OFF
          );
          setIsActive(data.isActive);
          setName(data.name);
          setTemperature(data.temperature);
          setDayStatus(
            WEEKDAY_NAMES.filter((day, i) => data.weekMap[i] !== "0")
          );
          setTime(new Date(`2002-02-02T${data.triggerTime}:00`));
        }
        setEnvironments(data.spaces);
      } catch (e) {
        // @ts-expect-error not mapping error responses
        setError(e.response.data.error as ErrorResponse);
      } finally {
        setIsLoading(false);
      }
    },
    [token, currentSpace, setError]
  );

  useEffect(() => {
    const FORBIDDEN_CHARACTERS = /[.|_|\-|&|\\|/|+]/gi;
    setIsNameValid(!FORBIDDEN_CHARACTERS.test(name));
  }, [name]);

  useEffect(() => {
    if (time) {
      setHoursText(String(time.getHours()).padStart(2, "0"));
      setMinutesText(String(time.getMinutes()).padStart(2, "0"));
      return;
    }

    setHoursText("");
    setMinutesText("");
  }, [time]);

  const commitTimeParts = () => {
    const paddedHours = hoursText.padStart(2, "0");
    const paddedMinutes = minutesText.padStart(2, "0");
    const parsed = parseTimeParts(paddedHours, paddedMinutes, time);

    if (parsed) {
      setTime(parsed);
      setHoursText(String(parsed.getHours()).padStart(2, "0"));
      setMinutesText(String(parsed.getMinutes()).padStart(2, "0"));
      return;
    }

    if (time) {
      setHoursText(String(time.getHours()).padStart(2, "0"));
      setMinutesText(String(time.getMinutes()).padStart(2, "0"));
      return;
    }

    setHoursText("");
    setMinutesText("");
  };

  useEffect(() => {
    // decides if a scene needs to be fetched on the first mount
    if (isLoading === "initial") {
      loadScene(id === "new" ? "" : id);
    }
  }, [id, isLoading, loadScene]);

  const handleSubmit = async () => {
    const resolvedTime =
      time ??
      parseTimeParts(
        hoursText.padStart(2, "0"),
        minutesText.padStart(2, "0"),
        null
      );

    if (
      name &&
      isNameValid &&
      action &&
      !!dayStatus.length &&
      resolvedTime !== null
    ) {
      setIsLoading("submit");

      const data = formatRequestData(
        name,
        action,
        environments,
        resolvedTime,
        currentSpace?.id || "",
        temperature,
        dayStatus,
        id || ""
      );
      try {
        await createOrUpdateScene(token, data);
        history.goBack();
      } catch (e) {
        // @ts-expect-error not mapping error responses
        setError(e.response.data.error as ErrorResponse);
      } finally {
        setIsLoading(false);
      }
    } else {
      setError(CustomCodes.INVALID_FORM);
    }
  };

  const handleFrequencyChange = (e, newState) => {
    setDayStatus(newState);
  };

  const handleSelection = (changedItem, isSelected) => {
    setEnvironments((prevVal) =>
      prevVal.map((item) => {
        if (item.id === changedItem.id) {
          return { ...item, isMember: isSelected };
        } else {
          return item;
        }
      })
    );
  };

  return (
    <PageStandard title="Eventos automatizados" displayLoading={!!isLoading}>
      <BlockStandard hideDivider>
        <TextField
          value={name}
          onChange={({ target }) => setName(target.value)}
          label="Nome da automação"
          error={!isNameValid || (!!error && !name)}
          helperText={
            !!error
              ? isNameValid
                ? "Esse campo é obrigatório"
                : "O nome contém caracteres inválidos"
              : ""
          }
        />
        {isActive !== null && (
          <Typography variant="caption">
            Esta cena está {isActive ? "ATIVA" : "DESATIVADA"}
          </Typography>
        )}
      </BlockStandard>

      <BlockStandard hideDivider>
        <Typography variant="h3" component="p">
          Ação:
        </Typography>
        <FormControl fullWidth>
          <InputLabel id="select-label">Selecione uma ação</InputLabel>
          <Select
            labelId="select-label"
            label="Selecione uma ação"
            value={action}
            error={!!error && !action}
            // @ts-expect-error the type is defined by the value in MenuItem
            onChange={({ target }) => setAction(target.value)}
          >
            <MenuItem value={SceneAction.AC_ON}>Ligar ar condicionado</MenuItem>
            <MenuItem value={SceneAction.AC_OFF}>
              Desligar ar condicionado
            </MenuItem>
          </Select>
        </FormControl>
      </BlockStandard>

      <BlockStandard hideDivider>
        <Typography variant="h3" component="p">
          Frequência:
        </Typography>
        <ToggleButtonGroup
          fullWidth
          value={dayStatus}
          className={classes.toggleButtonGroup}
          onChange={handleFrequencyChange}
        >
          {WEEKDAY_NAMES.map((day) => (
            <ToggleButton
              className={classes.toggleButton}
              size="large"
              key={day}
              value={day}
            >
              <Typography variant="body2" title={name}>
                {day[0]}
              </Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {!!error && !dayStatus.length && (
          <Typography variant="caption" color="error">
            Um evento precisa ocorrer em ao menos um dia
          </Typography>
        )}
      </BlockStandard>

      <BlockStandard hideDivider>
        <Typography variant="h3" component="p">
          Horário:
        </Typography>
        <Box className={classes.timeInputWrapper}>
          <Box className={classes.timeFieldsRow}>
            <TextField
              className={classes.timePartField}
              value={hoursText}
              onChange={({ target }) =>
                setHoursText(sanitizeTimePartInput(target.value))
              }
              onBlur={commitTimeParts}
              placeholder="00"
              inputProps={{
                inputMode: "numeric",
                maxLength: 2,
                "aria-label": "Hora",
              }}
            />
            <Typography className={classes.timeSeparator} aria-hidden>
              :
            </Typography>
            <TextField
              className={classes.timePartField}
              value={minutesText}
              onChange={({ target }) =>
                setMinutesText(sanitizeTimePartInput(target.value))
              }
              onBlur={commitTimeParts}
              placeholder="00"
              inputProps={{
                inputMode: "numeric",
                maxLength: 2,
                "aria-label": "Minuto",
              }}
            />
          </Box>
          <Box className={classes.timeLabelsRow}>
            <Typography component="span" className={classes.timePartLabel}>
              Hora
            </Typography>
            <Typography
              className={classes.timeSeparator}
              sx={{ visibility: "hidden" }}
              aria-hidden
            >
              :
            </Typography>
            <Typography component="span" className={classes.timePartLabel}>
              Minuto
            </Typography>
          </Box>
          <Typography component="p" className={classes.timeHint}>
            💡 Use o formato de 24h (ex: 14h em vez de 2h da tarde).
          </Typography>
        </Box>
        {!!error && time === null && (
          <Typography variant="caption" color="error">
            Um evento precisa ter horário definido
          </Typography>
        )}
      </BlockStandard>

      {action === SceneAction.AC_ON && (
        <BlockStandard hideDivider>
          <Typography variant="h3" component="p">
            Temperatura do ar condicionado:
          </Typography>
          <Thermostat
            temperature={temperature}
            onChange={(newTemp) => {
              setTemperature(newTemp);
            }}
          />
        </BlockStandard>
      )}

      <BlockStandard title="Ambientes inseridos na cena" hideDivider>
        <Alert severity="warning" variant="outlined">
          Ambientes sem dispositivos ONLINE não podem ser ligados a um evento ou
          removidos dele.
        </Alert>
        <FilteredList
          data={environments}
          getKey={(item) => item.id}
          getName={(item) => item.name}
          // @ts-expect-error mismatched typing, tbf
          ListItemComponent={ListItemSpaces}
          onItemSelect={handleSelection}
          isLoading={isLoading && isLoading !== "submit"}
        />
      </BlockStandard>

      <BlockStandard hideDivider>
        <Button onClick={handleSubmit} fullWidth variant="contained">
          Salvar
        </Button>
        <Button variant="outlined" fullWidth onClick={() => history.goBack()}>
          Cancelar
        </Button>
      </BlockStandard>
    </PageStandard>
  );
};
